import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Eraser, Pencil, Type } from "lucide-react";

/**
 * SignaturePad — draw or type a signature.
 * Returns the signature as a PNG data URL via onChange(dataUrl).
 */
export default function SignaturePad({ value, onChange, name, onNameChange, testId = "signature-pad" }) {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState("draw"); // draw | type
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [typed, setTyped] = useState("");

  // Resize canvas to its CSS size with HiDPI support
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#2A0F5A";
  }, [mode]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };
  const end = (e) => {
    if (!drawing) return;
    e?.preventDefault?.();
    setDrawing(false);
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange?.("");
  };

  const renderTyped = (text) => {
    setTyped(text);
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const r = c.getBoundingClientRect();
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = "#2A0F5A";
    ctx.font = "italic 38px 'Cormorant Garamond', Georgia, serif";
    ctx.textBaseline = "middle";
    ctx.fillText(text || "—", 16, r.height / 2);
    setHasInk(!!text);
    onChange?.(text ? c.toDataURL("image/png") : "");
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => { setMode("draw"); setTyped(""); clear(); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.18em] uppercase border ${mode === "draw" ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0] hover:border-[#7B56C9]"}`}
          data-testid="sigmode-draw"
        >
          <Pencil size={12} /> Draw
        </button>
        <button
          type="button"
          onClick={() => { setMode("type"); clear(); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.18em] uppercase border ${mode === "type" ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0] hover:border-[#7B56C9]"}`}
          data-testid="sigmode-type"
        >
          <Type size={12} /> Type
        </button>
        <div className="ml-auto">
          <button
            type="button"
            onClick={() => (mode === "type" ? renderTyped("") : clear())}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.18em] uppercase border border-[#E8E3F0] bg-white hover:border-[#7B56C9]"
            data-testid="sig-clear-btn"
          >
            <Eraser size={12} /> Clear
          </button>
        </div>
      </div>

      <div className="border border-[#E8E3F0] bg-[#FAFAF8] relative" style={{ height: 140 }}>
        <canvas
          ref={canvasRef}
          className={`w-full h-full ${mode === "draw" ? "cursor-crosshair" : "pointer-events-none"}`}
          onMouseDown={mode === "draw" ? start : undefined}
          onMouseMove={mode === "draw" ? move : undefined}
          onMouseUp={mode === "draw" ? end : undefined}
          onMouseLeave={mode === "draw" ? end : undefined}
          onTouchStart={mode === "draw" ? start : undefined}
          onTouchMove={mode === "draw" ? move : undefined}
          onTouchEnd={mode === "draw" ? end : undefined}
          data-testid="signature-canvas"
        />
        {!hasInk && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-[#7B56C9]/40 italic font-serif text-xl select-none">
            {mode === "draw" ? "Sign here" : "Type your name…"}
          </div>
        )}
      </div>

      {mode === "type" && (
        <Input
          placeholder="Type your name to generate signature"
          value={typed}
          onChange={(e) => renderTyped(e.target.value)}
          className="rounded-none border-[#E8E3F0]"
          data-testid="signature-typed-input"
        />
      )}

      {onNameChange !== undefined && (
        <div>
          <Label className="label-eyebrow mb-1.5 block">Full name</Label>
          <Input
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="As it should appear on the document"
            className="rounded-none border-[#E8E3F0]"
            data-testid="signature-name-input"
          />
        </div>
      )}
    </div>
  );
}
